import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Users, UserPlus, Shield, User, Hash, Pencil, Clock, CheckCircle, XCircle, Trash2 } from "lucide-react";

const API = '/api';

export default function UsersPage() {
  const { getAuthHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [activeTab, setActiveTab] = useState("approved");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    role: "standard",
    shooter_id: "",
  });

  useEffect(() => {
    fetchUsers();
    fetchPendingUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, {
        headers: getAuthHeader(),
      });
      // Filter to only show approved users
      setUsers(res.data.filter(u => u.status !== "pending" && u.status !== "rejected"));
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const res = await axios.get(`${API}/users/pending`, {
        headers: getAuthHeader(),
      });
      setPendingUsers(res.data);
    } catch (error) {
      console.error("Failed to fetch pending users");
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          role: formData.role,
          shooter_id: formData.shooter_id || null,
        };
        await axios.put(`${API}/users/${editingUser.id}`, updateData, {
          headers: getAuthHeader(),
        });
        toast.success("User updated");
      } else {
        // Create new user
        await axios.post(`${API}/auth/create-user`, formData, {
          headers: getAuthHeader(),
        });
        toast.success("User created");
      }
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || "",
      password: "",
      role: user.role,
      shooter_id: user.shooter_id || "",
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({ name: "", email: "", phone: "", password: "", role: "standard", shooter_id: "" });
  };

  const handleApproveUser = async (userId, approved) => {
    try {
      await axios.post(`${API}/users/${userId}/approve`, 
        { status: approved ? "approved" : "rejected" },
        { headers: getAuthHeader() }
      );
      toast.success(approved ? "User approved" : "User rejected");
      fetchPendingUsers();
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (userId === currentUser.id) {
      toast.error("Cannot change your own role");
      return;
    }
    try {
      await axios.put(`${API}/users/${userId}/role`, { role: newRole }, {
        headers: getAuthHeader(),
      });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser.id) {
      toast.error("Cannot delete your own account");
      return;
    }
    if (!window.confirm("Are you sure you want to delete this user? This action cannot be undone.")) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${userId}`, {
        headers: getAuthHeader(),
      });
      toast.success("User deleted");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-500">Loading...</div>
      </div>
    );
  }

  return (
    <div data-testid="users-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage users and approve registrations</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              onClick={() => { resetForm(); setDialogOpen(true); }}
              className="bg-[#1B3B6F] hover:bg-[#1B3B6F]/90"
            >
              <UserPlus size={20} className="mr-2" />
              Create User
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[450px]">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl">
                {editingUser ? "Edit User" : "Create New User"}
              </DialogTitle>
              <DialogDescription>
                {editingUser ? "Update user information" : "Create a new user account with specified role"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="shooter_id">Shooter ID</Label>
                <Input
                  id="shooter_id"
                  value={formData.shooter_id}
                  onChange={(e) => setFormData({ ...formData, shooter_id: e.target.value })}
                  placeholder="e.g., 001, A-123"
                />
                <p className="text-xs text-slate-500 mt-1">Unique shooter identifier for results</p>
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone (optional)</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              {!editingUser && (
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!editingUser}
                    minLength={6}
                  />
                </div>
              )}
              <div>
                <Label htmlFor="role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(val) => setFormData({ ...formData, role: val })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard User</SelectItem>
                    <SelectItem value="admin">Administrator</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-[#1B3B6F] hover:bg-[#1B3B6F]/90">
                {editingUser ? "Update User" : "Create User"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Approved and Pending Users */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="approved" className="flex items-center gap-2">
            <CheckCircle size={16} />
            Approved Users ({users.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pending Approval ({pendingUsers.length})
            {pendingUsers.length > 0 && (
              <Badge variant="destructive" className="ml-1">{pendingUsers.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="approved">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Users className="text-[#1B3B6F]" />
                All Approved Users
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Shooter ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Role</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="font-mono text-sm">
                        {user.shooter_id ? (
                          <span className="flex items-center gap-1">
                            <Hash size={12} className="text-slate-400" />
                            {user.shooter_id}
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="font-medium">
                        {user.name}
                        {user.id === currentUser.id && (
                          <span className="text-xs text-slate-400 ml-2">(you)</span>
                        )}
                      </td>
                      <td className="text-slate-600">{user.email}</td>
                      <td className="text-slate-500">{user.phone || "-"}</td>
                      <td>
                        <Badge
                          variant={user.role === "admin" ? "default" : "secondary"}
                          className={user.role === "admin" ? "bg-amber-100 text-amber-800" : ""}
                        >
                          {user.role === "admin" ? (
                            <><Shield size={12} className="mr-1" /> Admin</>
                          ) : (
                            <><User size={12} className="mr-1" /> Standard</>
                          )}
                        </Badge>
                      </td>
                      <td>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditDialog(user)}
                          >
                            <Pencil size={14} />
                          </Button>
                          {user.id !== currentUser.id && (
                            <>
                              <Select
                                value={user.role}
                                onValueChange={(val) => handleRoleChange(user.id, val)}
                              >
                                <SelectTrigger className="w-28">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="standard">Standard</SelectItem>
                                  <SelectItem value="admin">Admin</SelectItem>
                                </SelectContent>
                              </Select>
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeleteUser(user.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </>
                          )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="pending">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl flex items-center gap-2">
                <Clock className="text-amber-600" />
                Pending Registrations
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {pendingUsers.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  No pending registrations
                </div>
              ) : (
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Registered</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((user) => (
                      <tr key={user.id}>
                        <td className="font-medium">{user.name}</td>
                        <td className="text-slate-600">{user.email}</td>
                        <td className="text-slate-500">{user.phone || "-"}</td>
                        <td className="text-slate-500 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleApproveUser(user.id, true)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle size={14} className="mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleApproveUser(user.id, false)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <XCircle size={14} className="mr-1" />
                              Reject
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info */}
      <div className="text-sm text-slate-500 text-center">
        <p>Admins can access Payments, Sponsors, and User Management pages.</p>
        <p>Standard users can only access Events, Matches, Score Entry, and Leaderboards.</p>
      </div>
    </div>
  );
}
